import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Edit2, Trash2, GripVertical, CalendarClock, Eye, HelpCircle, UserCog, Download, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { GradePickerDialog } from './GradePickerDialog';
import { ImportScheduleDialog } from './ImportScheduleDialog';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { HelpDialog } from '@/components/HelpDialog';
import {
  DndContext,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable row component
function SortableScheduleRow({ id, schedule, canEdit, canReorder, isSuperUser, onView, onEdit, onDelete, onAnnualReview, onReassignOwner, onExport }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id, disabled: !canReorder });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr ref={setNodeRef} style={style} className="border-b border-stone-100 hover:bg-stone-50">
      <td className="w-8 py-3 px-2">
        {canReorder ? (
          <button {...attributes} {...listeners} className="cursor-grab hover:text-green-700">
            <GripVertical className="h-4 w-4 text-stone-400" />
          </button>
        ) : (
          <span className="text-stone-200">
            <GripVertical className="h-4 w-4" />
          </span>
        )}
      </td>
      <td className="py-3 px-2 font-medium">
        {schedule.schedule_type}
        {schedule.is_draft && (
          <span className="ml-2 inline-block px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-800">
            Draft {schedule.review_year || ''}
          </span>
        )}
        {schedule.archived_at && (
          <span className="ml-2 inline-block px-2 py-0.5 text-xs rounded bg-stone-100 text-stone-600">
            Archived
          </span>
        )}
      </td>
      <td className="py-3 px-2 text-sm">{schedule.schedule_description}</td>
      <td className="py-3 px-2">
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => onView(schedule)} className="action-icon" title="View" data-testid={`view-schedule-${schedule.schedule_id}`}>
            <Eye className="h-4 w-4" />
          </button>
          {canEdit ? (
            <>
              <button
                onClick={() => onAnnualReview(schedule)}
                className="action-icon text-emerald-700 hover:text-emerald-900"
                title={schedule.is_draft ? 'Resume annual review' : 'Start annual review'}
                data-testid={`annual-review-${schedule.schedule_id}`}
              >
                <CalendarClock className="h-4 w-4" />
              </button>
              <button onClick={() => onEdit(schedule)} className="action-icon" title="Edit" data-testid={`edit-schedule-${schedule.schedule_id}`}>
                <Edit2 className="h-4 w-4" />
              </button>
              {isSuperUser && (
                <button
                  onClick={() => onReassignOwner(schedule)}
                  className="action-icon text-indigo-700 hover:text-indigo-900"
                  title="Reassign owner"
                  data-testid={`reassign-owner-${schedule.schedule_id}`}
                >
                  <UserCog className="h-4 w-4" />
                </button>
              )}
              {isSuperUser && (
                <button
                  onClick={() => onExport(schedule)}
                  className="action-icon text-sky-700 hover:text-sky-900"
                  title="Export schedule to Excel"
                  data-testid={`export-schedule-${schedule.schedule_id}`}
                >
                  <Download className="h-4 w-4" />
                </button>
              )}
              <button onClick={() => onDelete(schedule.schedule_id)} className="action-icon" title="Delete" data-testid={`delete-schedule-${schedule.schedule_id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

export function SchedulesTab({ schedules, setSchedules, fetchSchedules, handleDragEnd, sensors, currentUser, autoOpenAdd, onAutoOpenAddConsumed }) {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [yearDialogOpen, setYearDialogOpen] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewYear, setReviewYear] = useState(new Date().getFullYear() + 1);
  const [composePickerOpen, setComposePickerOpen] = useState(false);
  const [formData, setFormData] = useState({ 
    schedule_type: '', 
    schedule_description: '',
    special_event: false,
    two_round_events: false,
    skate_mf_together: true,
    judge_mf_together: true
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewOnly, setViewOnly] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [reassignTarget, setReassignTarget] = useState(null);
  const [reassignUserId, setReassignUserId] = useState('');
  const [reassignIncludeChildren, setReassignIncludeChildren] = useState(false);
  const [reassignSaving, setReassignSaving] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  // Import / Export (Super User only)
  const [importOpen, setImportOpen] = useState(false);
  
  const isSuperUser = currentUser?.role === 'super';
  const currentUserId = currentUser?.user_id;
  
  // Check if user can edit a specific item
  const canEditItem = (item) => {
    return isSuperUser || item.created_by === currentUserId || item.owner_user_id === currentUserId;
  };

  const handleExport = async (schedule) => {
    try {
      const res = await axios.get(
        `${API}/schedules/${schedule.schedule_id}/export`,
        { withCredentials: true, responseType: 'blob' },
      );
      const safe = (schedule.schedule_type || 'schedule').replace(/[\\/\s]+/g, '_');
      const date = new Date().toISOString().slice(0, 10);
      const filename = `Schedule-${safe}-${date}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Schedule exported');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to export schedule');
    }
  };

  const openImport = () => {
    setImportOpen(true);
  };

  const openReassign = async (schedule) => {
    setReassignTarget(schedule);
    setReassignUserId(schedule.created_by || '');
    setReassignIncludeChildren(false);
    if (allUsers.length === 0) {
      try {
        const res = await axios.get(`${API}/users`, { withCredentials: true });
        setAllUsers(res.data || []);
      } catch {
        toast.error('Could not load users list');
      }
    }
  };

  const submitReassign = async () => {
    if (!reassignTarget || !reassignUserId) return;
    if (reassignUserId === reassignTarget.created_by) {
      toast.info('That user already owns this schedule');
      return;
    }
    setReassignSaving(true);
    try {
      const res = await axios.post(
        `${API}/content/schedules/${reassignTarget.schedule_id}/reassign-owner`,
        { new_owner_user_id: reassignUserId, include_children: reassignIncludeChildren },
        { withCredentials: true }
      );
      const target = allUsers.find(u => u.user_id === reassignUserId);
      const label = target ? `${target.first_name || ''} ${target.last_name || ''}`.trim() || target.email : 'the selected user';
      const g = res.data?.grades_updated ?? 0;
      const t = res.data?.timings_updated ?? 0;
      const extras = reassignIncludeChildren
        ? ` (also reassigned ${g} grade${g === 1 ? '' : 's'} and ${t} timing record${t === 1 ? '' : 's'})`
        : '';
      toast.success(`Schedule ownership reassigned to ${label}${extras}`);
      setReassignTarget(null);
      fetchSchedules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reassign owner');
    } finally {
      setReassignSaving(false);
    }
  };

  const handleAnnualReview = (schedule) => {
    if (schedule.is_draft) {
      navigate(`/content/schedules/${schedule.schedule_id}/annual-review`);
      return;
    }
    setReviewTarget(schedule);
    setYearDialogOpen(true);
  };

  const startAnnualReview = async () => {
    try {
      const r = await axios.post(
        `${API}/schedules/${reviewTarget.schedule_id}/annual-review/start`,
        { review_year: Number(reviewYear) },
        { withCredentials: true }
      );
      toast.success('Annual review started.');
      setYearDialogOpen(false);
      navigate(`/content/schedules/${r.data.draft_schedule_id}/annual-review`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      // If a draft already exists the backend returns {message, draft_schedule_id}
      if (detail && typeof detail === 'object' && detail.draft_schedule_id) {
        toast.info(detail.message);
        setYearDialogOpen(false);
        navigate(`/content/schedules/${detail.draft_schedule_id}/annual-review`);
        return;
      }
      toast.error(detail || 'Could not start annual review');
    }
  };
  
  const resetForm = () => {
    setFormData({ 
      schedule_type: '', 
      schedule_description: '',
      special_event: false,
      two_round_events: false,
      skate_mf_together: true,
      judge_mf_together: true
    });
    setEditingItem(null);
    setViewOnly(false);
  };
  
  const handleEdit = (schedule) => {
    setFormData({
      schedule_type: schedule.schedule_type,
      schedule_description: schedule.schedule_description,
      special_event: schedule.special_event ?? false,
      two_round_events: schedule.two_round_events ?? false,
      skate_mf_together: schedule.skate_mf_together ?? true,
      judge_mf_together: schedule.judge_mf_together ?? true
    });
    setEditingItem(schedule);
    setViewOnly(false);
    setDialogOpen(true);
  };

  const handleView = (schedule) => {
    handleEdit(schedule);
    setViewOnly(true);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.patch(`${API}/content/schedules/${editingItem.schedule_id}`, formData, { withCredentials: true });
        toast.success('Schedule updated!');
      } else {
        await axios.post(`${API}/content/schedules`, formData, { withCredentials: true });
        toast.success('Schedule created!');
      }
      setDialogOpen(false);
      resetForm();
      fetchSchedules();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || 'Permission denied');
      } else {
        toast.error('Failed to save schedule');
      }
    }
  };
  
  const handleDeleteClick = (id) => {
    const schedule = schedules.find(s => s.schedule_id === id);
    setItemToDelete(schedule);
    setDeleteDialogOpen(true);
  };
  
  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/content/schedules/${itemToDelete.schedule_id}`, { withCredentials: true });
      toast.success('Schedule deleted');
      fetchSchedules();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || 'Permission denied');
      } else {
        toast.error('Failed to delete schedule');
      }
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };
  
  // External trigger (e.g. dashboard Quick Action `?action=create`) to open
  // the unified Add Schedule flow on this tab.
  useEffect(() => {
    if (autoOpenAdd) {
      setComposePickerOpen(true);
      onAutoOpenAddConsumed?.();
    }
  }, [autoOpenAdd, onAutoOpenAddConsumed]);

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Schedules</h2>
        <div className="flex gap-2">
          {isSuperUser && (
            <Button
              onClick={openImport}
              variant="outline"
              data-testid="import-schedule-button"
            >
              <Upload className="mr-2 h-4 w-4" />
              Import New Schedule
            </Button>
          )}
          <Button
            onClick={() => setComposePickerOpen(true)}
            className="btn-primary"
            data-testid="add-schedule-button"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Schedule
          </Button>
        </div>
      </div>
      
      {!isSuperUser && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          You can create new schedules and edit/delete your own items. Items created by others are view-only.
        </div>
      )}
      
      <Card className="p-6">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(e, 'schedules')}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-stone-200">
                  <th className="w-8"></th>
                  <th className="text-left py-3 px-2 font-semibold">Schedule Name</th>
                  <th className="text-left py-3 px-2 font-semibold">Description</th>
                  <th className="text-center py-3 px-2 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                <SortableContext items={schedules.map(s => s.schedule_id)} strategy={verticalListSortingStrategy}>
                  {schedules.map(schedule => (
                    <SortableScheduleRow
                      key={schedule.schedule_id}
                      id={schedule.schedule_id}
                      schedule={schedule}
                      canEdit={canEditItem(schedule)}
                      canReorder={isSuperUser}
                      isSuperUser={isSuperUser}
                      onView={handleView}
                      onEdit={handleEdit}
                      onDelete={handleDeleteClick}
                      onAnnualReview={handleAnnualReview}
                      onReassignOwner={openReassign}
                      onExport={handleExport}
                    />
                  ))}
                </SortableContext>
              </tbody>
            </table>
            {schedules.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                No schedules yet. Create your first one!
              </div>
            )}
          </div>
        </DndContext>
      </Card>
      
      <Dialog open={dialogOpen} onOpenChange={(val) => { setDialogOpen(val); if (!val) resetForm(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{viewOnly ? 'View Schedule' : editingItem ? 'Edit Schedule' : 'Create Schedule'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Schedule Type *</Label>
              <Input value={formData.schedule_type} onChange={(e) => setFormData({...formData, schedule_type: e.target.value})} required disabled={viewOnly} data-testid="schedule-type-input" />
            </div>
            <div>
              <Label>Description *</Label>
              <Input value={formData.schedule_description} onChange={(e) => setFormData({...formData, schedule_description: e.target.value})} required disabled={viewOnly} data-testid="schedule-description-input" />
            </div>
            
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center gap-2 mb-3">
                <Label className="text-sm font-semibold text-[#2D4A3E]">Schedule Settings</Label>
                <button
                  type="button"
                  onClick={() => setHelpOpen(true)}
                  className="text-stone-400 hover:text-stone-600 transition-colors"
                  title="What do these settings do?"
                  data-testid="schedule-settings-help-btn"
                >
                  <HelpCircle className="h-4 w-4" />
                </button>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Checkbox 
                    id="special_event"
                    checked={formData.special_event}
                    onCheckedChange={(checked) => setFormData({...formData, special_event: checked})}
                    disabled={viewOnly}
                    data-testid="special-event-checkbox"
                  />
                  <Label htmlFor="special_event" className="text-sm font-normal cursor-pointer">
                    Custom Event Schedule
                  </Label>
                </div>
                
                <div className="flex items-center gap-3">
                  <Checkbox 
                    id="two_round_events"
                    checked={formData.two_round_events}
                    onCheckedChange={(checked) => setFormData({...formData, two_round_events: checked})}
                    disabled={viewOnly}
                    data-testid="two-round-checkbox"
                  />
                  <Label htmlFor="two_round_events" className="text-sm font-normal cursor-pointer">
                    Has Two Round Events
                  </Label>
                </div>
                
                <div className="flex items-center gap-3">
                  <Checkbox 
                    id="skate_mf_together"
                    checked={formData.skate_mf_together}
                    onCheckedChange={(checked) => setFormData({...formData, skate_mf_together: checked})}
                    disabled={viewOnly}
                    data-testid="skate-together-checkbox"
                  />
                  <Label htmlFor="skate_mf_together" className="text-sm font-normal cursor-pointer">
                    Skate M/F Together
                  </Label>
                </div>
                
                <div className="flex items-center gap-3">
                  <Checkbox 
                    id="judge_mf_together"
                    checked={formData.judge_mf_together}
                    onCheckedChange={(checked) => setFormData({...formData, judge_mf_together: checked})}
                    disabled={viewOnly}
                    data-testid="judge-together-checkbox"
                  />
                  <Label htmlFor="judge_mf_together" className="text-sm font-normal cursor-pointer">
                    Judge M/F Together
                  </Label>
                </div>
              </div>
            </div>
            
            {!viewOnly && (
              <Button type="submit" className="w-full btn-primary" data-testid="schedule-submit-button">
                {editingItem ? 'Update Schedule' : 'Create Schedule'}
              </Button>
            )}
          </form>
        </DialogContent>
      </Dialog>

      <HelpDialog open={helpOpen} onOpenChange={setHelpOpen} topicKey="schedule-settings" />
      
      <Dialog open={yearDialogOpen} onOpenChange={setYearDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start annual review — {reviewTarget?.schedule_type}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              A clone of this schedule (with all grades) will be created as a draft. The current
              schedule stays live until you publish the new year.
            </p>
            <div>
              <Label>Review year *</Label>
              <Input
                type="number"
                value={reviewYear}
                min={2020}
                max={2050}
                onChange={(e) => setReviewYear(e.target.value)}
                data-testid="annual-review-year-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setYearDialogOpen(false)}>Cancel</Button>
              <Button className="btn-primary" onClick={startAnnualReview} data-testid="annual-review-start-btn">
                Start review
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <GradePickerDialog
        open={composePickerOpen}
        onOpenChange={setComposePickerOpen}
        mode="compose"
        onCompleted={() => fetchSchedules()}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        loading={deleting}
        variant="critical"
        title="Do you want to delete this schedule?"
        prompt={(
          <p className="text-stone-700">
            You've asked to delete this schedule. All grades associated which are part of
            this schedule will also be deleted &mdash; do you want to proceed?
          </p>
        )}
        confirmLabel="Yes"
        cancelLabel="No"
      />

      <Dialog open={!!reassignTarget} onOpenChange={(v) => { if (!v) setReassignTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="reassign-owner-dialog">
          <DialogHeader>
            <DialogTitle>Reassign schedule owner</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Hand full edit rights on <strong>{reassignTarget?.schedule_type}</strong> over to another user.
              This updates the schedule's owner (<code>created_by</code>) — the field every downstream
              permission check reads (schedule edit/delete, Event Timings, stale-link repair). Timing
              rows and grades already saved are untouched.
            </p>
            <div>
              <Label className="text-sm">Current owner</Label>
              <div className="text-sm mt-1 text-stone-700">
                {(() => {
                  const cur = allUsers.find(u => u.user_id === reassignTarget?.created_by);
                  if (!cur) return reassignTarget?.created_by || '(unknown)';
                  const name = `${cur.first_name || ''} ${cur.last_name || ''}`.trim();
                  return name ? `${name} · ${cur.email}` : cur.email;
                })()}
              </div>
            </div>
            <div>
              <Label className="text-sm">New owner *</Label>
              <select
                value={reassignUserId}
                onChange={(e) => setReassignUserId(e.target.value)}
                className="mt-1 w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm shadow-sm focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                data-testid="reassign-owner-select"
              >
                <option value="">Select a user…</option>
                {allUsers
                  .slice()
                  .sort((a, b) => {
                    const an = `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.email || '';
                    const bn = `${b.first_name || ''} ${b.last_name || ''}`.trim() || b.email || '';
                    return an.localeCompare(bn);
                  })
                  .map(u => {
                    const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
                    const label = name ? `${name} · ${u.email}` : u.email;
                    return (
                      <option key={u.user_id} value={u.user_id}>
                        {label}{u.role === 'super' ? ' (super)' : ''}
                      </option>
                    );
                  })}
              </select>
            </div>
            <div className="flex items-start gap-3 pt-1">
              <Checkbox
                id="reassign-include-children"
                checked={reassignIncludeChildren}
                onCheckedChange={(v) => setReassignIncludeChildren(!!v)}
                data-testid="reassign-include-children-checkbox"
              />
              <Label htmlFor="reassign-include-children" className="text-sm font-normal cursor-pointer leading-snug">
                …and reassign this schedule's grades and timing records too
                <span className="block text-xs text-muted-foreground mt-0.5">
                  Flips <code>created_by</code> on every grade and event-timing row belonging to this schedule so audit stamps stay in sync with the new owner.
                </span>
              </Label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setReassignTarget(null)} disabled={reassignSaving}>Cancel</Button>
              <Button
                className="btn-primary"
                onClick={submitReassign}
                disabled={reassignSaving || !reassignUserId || reassignUserId === reassignTarget?.created_by}
                data-testid="reassign-owner-submit"
              >
                {reassignSaving ? 'Reassigning…' : 'Reassign owner'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ImportScheduleDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={fetchSchedules}
      />
    </>
  );
}